import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Typography,
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import AppVersionService from "../../pages/dashboard/Locality/Service/appVersionService";

const POLL_MS = 60 * 1000;
const DEFAULT_TITLE = "Update available";
const DEFAULT_MESSAGE =
  "New changes have occurred. Kindly refresh the web page.";

/** Version the user loaded with (in-memory; resets on full page reload). */
let loadedWebVersion = null;

const unwrapConfig = (res) => {
  if (!res) return null;
  const inner = res.data != null && typeof res.data === "object" ? res.data : res;
  if (inner && (inner.web !== undefined || inner._id)) return inner;
  return null;
};

export default function WebVersionNotifier() {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(DEFAULT_TITLE);
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [forceUpdate, setForceUpdate] = useState(false);

  const checkVersion = useCallback(async () => {
    try {
      const res = await AppVersionService.get();
      const config = unwrapConfig(res);
      if (!config) return;

      const serverWeb = String(config.web || "").trim();
      if (!serverWeb) return;

      if (loadedWebVersion == null) {
        loadedWebVersion = serverWeb;
        return;
      }

      if (serverWeb !== loadedWebVersion) {
        setTitle(String(config.updateTitle || config.title || "").trim() || DEFAULT_TITLE);
        setMessage(
          String(config.updateMessage || config.message || "").trim() || DEFAULT_MESSAGE
        );
        setForceUpdate(Boolean(config.forceUpdate));
        setOpen(true);
      }
    } catch {
      // Config may not exist yet; ignore until it is created.
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const intervalId = window.setInterval(checkVersion, POLL_MS);
    const onFocus = () => checkVersion();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
    };
  }, [checkVersion]);

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleDismiss = () => {
    if (!forceUpdate) setOpen(false);
  };

  if (forceUpdate && open) {
    return (
      <Dialog open disableEscapeKeyDown onClose={() => {}} maxWidth="xs" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            {message}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            Update now
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={open}
      onClose={handleDismiss}
      message={
        <Box>
          <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
            {title}
          </Typography>
          <Typography variant="body2">{message}</Typography>
        </Box>
      }
      action={
        <Button color="secondary" size="small" onClick={handleRefresh}>
          Refresh
        </Button>
      }
    />
  );
}
